import {useEffect, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import './CheckoutConsentModal.css'

interface Props {
  packageName: string
  credits: number
  priceLabel: string
  loading: boolean
  errorKey: string | null
  onConfirm: () => void
  onClose: () => void
}

const CONSENT_LINK_COMPONENTS = {
  refund: <RouterLink to="/refund" target="_blank" rel="noreferrer" />,
  terms: <RouterLink to="/terms" target="_blank" rel="noreferrer" />,
  strong: <strong />,
}

// Mount/unmount-driven: the parent gates rendering by presence (not an `open` prop), so each
// open is a fresh mount → `checked` starts false without a reset effect.
const CheckoutConsentModal = ({packageName, credits, priceLabel, loading, errorKey, onConfirm, onClose}: Props) => {
  const {t} = useTranslation()
  const [checked, setChecked] = useState(false)

  // Close on Escape (only when not mid-redirect)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [loading, onClose])

  const handleBackdrop = () => {
    if (!loading) onClose()
  }

  const confirmDisabled = !checked || loading

  return (
    <div className="cc-backdrop" onClick={handleBackdrop} role="presentation">
      <div
        className="cc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cc-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cc-modal-title" className="cc-title">
          {t('pricing.consentModal.title')}
        </h3>

        <p className="cc-summary">
          <Trans
            i18nKey="pricing.consentModal.summary"
            values={{packageName, credits, price: priceLabel}}
            components={{strong: <strong />}}
          />
        </p>

        <label className="cc-consent-row">
          <input
            type="checkbox"
            checked={checked}
            disabled={loading}
            onChange={(e) => setChecked(e.target.checked)}
            aria-describedby="cc-consent-text"
          />
          <span id="cc-consent-text" className="cc-consent-text">
            <Trans i18nKey="pricing.consentModal.consentText" components={CONSENT_LINK_COMPONENTS} />
          </span>
        </label>

        {errorKey && <p className="cc-error">{t(errorKey)}</p>}

        <div className="cc-actions">
          <button type="button" className="cc-btn cc-btn-ghost" onClick={onClose} disabled={loading}>
            {t('pricing.consentModal.cancelLabel')}
          </button>
          <button
            type="button"
            className="cc-btn cc-btn-primary"
            onClick={onConfirm}
            disabled={confirmDisabled}
            aria-busy={loading || undefined}
          >
            {loading ? t('pricing.consentModal.loadingLabel') : t('pricing.consentModal.confirmLabel')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CheckoutConsentModal
